"use client"

import { createPortal } from "react-dom"
import { useCallback, useEffect, useState } from "react"
import { useParams, useRouter } from "next/navigation"
import Image from "@modules/common/components/cdn-image"
import { HttpTypes } from "@medusajs/types"
import { addToCart } from "@lib/data/cart"
import { recoverFromStaleDeploy } from "@lib/util/stale-deploy"
import { notifyCartChanged } from "@lib/context/cart-context"
import { useTracking } from "@lib/analytics/tracking-context"
import { getDisplayTitle } from "@lib/util/get-display-title"

// ── Shared "Quick add" affordance for the global product card ──────────────────
// Generalized from the collection lookbook card so the SAME quick-add works on
// the homepage rails, collection pages and category pages. Typed to the standard
// Medusa StoreProduct; renders nothing when the product has no variant/option
// data (so a card fed by a minimal field set degrades gracefully instead of
// breaking the add-to-cart variant match).

const SIZE_ORDER = ["XS", "S", "M", "L", "XL", "2XL", "XXL", "3XL", "4XL", "5XL"]

type SProduct = HttpTypes.StoreProduct
type SVariant = NonNullable<SProduct["variants"]>[number]
type CalcPrice = {
  calculated_amount: number
  original_amount: number
  currency_code: string
}

function sortOptionValues(title: string, values: string[]): string[] {
  if (!title.toLowerCase().includes("size")) return values
  return [...values].sort((a, b) => {
    const norm = (v: string) => v.trim().toUpperCase().replace(/\s+/g, "")
    const ai = SIZE_ORDER.indexOf(norm(a))
    const bi = SIZE_ORDER.indexOf(norm(b))
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi) || a.localeCompare(b)
  })
}

function variantInStock(v: SVariant): boolean {
  if (v.manage_inventory === false) return true
  if ((v as any).allow_backorder === true) return true
  return ((v.inventory_quantity as number | undefined) ?? 0) > 0
}

function fmtPrice(amount: number, currency: string): string {
  if (currency.toLowerCase() === "bdt") {
    return `৳${new Intl.NumberFormat("en-BD", { maximumFractionDigits: 0 }).format(amount)}`
  }
  return `${currency.toUpperCase()} ${amount}`
}

// ── Quick View Modal ──────────────────────────────────────────────────────────

function QuickViewModal({
  product,
  swatchMap,
  onClose,
}: {
  product: SProduct
  swatchMap: Record<string, string>
  onClose: () => void
}) {
  const { countryCode } = useParams() as { countryCode: string }
  const router = useRouter()
  const { trackAddToCart } = useTracking()

  const displayTitle = getDisplayTitle(product)
  const options = product.options || []
  const variants = product.variants || []

  // Pre-select first in-stock variant's values
  const [selected, setSelected] = useState<Record<string, string>>(() => {
    const first = variants.find(variantInStock) ?? variants[0]
    if (!first?.options) return {}
    const init: Record<string, string> = {}
    options.forEach((opt) => {
      const match = first.options?.find((vo) =>
        (opt.values || []).some((ov) => ov.value === vo.value)
      )
      if (match) init[opt.title] = match.value
    })
    return init
  })

  const [isAdding, setIsAdding] = useState(false)
  const [isBuying, setIsBuying] = useState(false)
  const [added, setAdded] = useState(false)

  // Find matching variant — all selected values must appear in variant options
  const selectedVariant = variants.find((v) =>
    Object.values(selected).every((val) => v.options?.some((o) => o.value === val))
  )

  const inStock = selectedVariant ? variantInStock(selectedVariant) : false

  const isAvailable = (optTitle: string, val: string) =>
    variants.some(
      (v) =>
        v.options?.some((o) => o.value === val) &&
        Object.entries(selected)
          .filter(([t]) => t !== optTitle)
          .every(([, sv]) => v.options?.some((o) => o.value === sv))
    )

  const isInStock = (optTitle: string, val: string) =>
    variants.some(
      (v) =>
        v.options?.some((o) => o.value === val) &&
        Object.entries(selected)
          .filter(([t]) => t !== optTitle)
          .every(([, sv]) => v.options?.some((o) => o.value === sv)) &&
        variantInStock(v)
    )

  // Price display (with original/strikethrough when on a sale price list)
  const priceInfo = (() => {
    const cp = selectedVariant?.calculated_price as CalcPrice | undefined
    if (cp) {
      const isSale =
        typeof cp.original_amount === "number" &&
        cp.original_amount > cp.calculated_amount
      return {
        prefix: "",
        calculated: fmtPrice(cp.calculated_amount, cp.currency_code),
        original: isSale ? fmtPrice(cp.original_amount, cp.currency_code) : null,
        isSale,
      }
    }
    const priced = variants.filter(
      (v) => typeof (v.calculated_price as CalcPrice | undefined)?.calculated_amount === "number"
    )
    if (!priced.length) return null
    const cheapest = priced.reduce((a, b) =>
      ((a.calculated_price as CalcPrice).calculated_amount <=
      (b.calculated_price as CalcPrice).calculated_amount)
        ? a
        : b
    )
    const c = cheapest.calculated_price as CalcPrice
    const isSale =
      typeof c.original_amount === "number" &&
      c.original_amount > c.calculated_amount
    return {
      prefix: "From ",
      calculated: fmtPrice(c.calculated_amount, c.currency_code),
      original: isSale ? fmtPrice(c.original_amount, c.currency_code) : null,
      isSale,
    }
  })()

  const stockQty = selectedVariant?.inventory_quantity as number | undefined
  const lowStock =
    selectedVariant?.manage_inventory !== false &&
    typeof stockQty === "number" &&
    stockQty > 0 &&
    stockQty < 5

  const handleAddToCart = async () => {
    if (!selectedVariant?.id) return
    setIsAdding(true)
    try {
      await addToCart({ variantId: selectedVariant.id, quantity: 1, countryCode })
      trackAddToCart(product as any, selectedVariant as any, 1)
      notifyCartChanged()
      setAdded(true)
      window.setTimeout(() => onClose(), 650)
    } catch (error) {
      // Stale tab from before a redeploy → reload onto the current build once.
      recoverFromStaleDeploy(error)
      // silent otherwise — user can retry
    } finally {
      setIsAdding(false)
    }
  }

  const handleBuyNow = async () => {
    if (!selectedVariant?.id) return
    setIsBuying(true)
    try {
      await addToCart({ variantId: selectedVariant.id, quantity: 1, countryCode })
      trackAddToCart(product as any, selectedVariant as any, 1)
      notifyCartChanged()
      router.push("/checkout?step=address")
    } catch (error) {
      if (recoverFromStaleDeploy(error)) return
      setIsBuying(false)
    }
  }

  // Lock scroll + ESC close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    document.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [onClose])

  const styleCode =
    (product.metadata?.style_code as string | undefined) ||
    product.handle?.toUpperCase()?.slice(0, 10)

  const heroImage = product.images?.[0]?.url ?? product.thumbnail ?? null
  const canSubmit = !!selectedVariant && inStock && !isAdding && !isBuying

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center bg-black/60 p-0 small:items-center small:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Quick add — ${displayTitle}`}
    >
      <div
        className="flex w-full max-h-[92dvh] flex-col overflow-y-auto bg-[#FFFFFF] shadow-2xl small:max-w-[680px] small:flex-row small:overflow-hidden small:rounded-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Left: product image ── */}
        <div className="relative h-[56vw] w-full shrink-0 bg-[#FFFFFF] small:h-auto small:w-[42%]">
          {heroImage ? (
            <Image
              src={heroImage}
              alt={displayTitle}
              fill
              sizes="(min-width: 640px) 280px, 100vw"
              className="object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-[#EBEBEB]" />
          )}

          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center bg-white/90 text-[#1A1612] transition hover:bg-white"
            aria-label="Close"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Right: content ── */}
        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-6 small:px-8 small:py-8">

          <div>
            {styleCode && (
              <p className="font-sans text-[10px] uppercase tracking-[2.5px] text-[#3D5240]">
                {styleCode}
              </p>
            )}
            <h2 className="font-display mt-1 text-[22px] leading-tight text-[#1A1612] small:text-[24px]">
              {displayTitle}
            </h2>
            {priceInfo && (
              <p className="mt-1.5 flex items-baseline gap-2 font-sans text-[15px] font-medium">
                <span className={priceInfo.isSale ? "text-[#b85c00]" : "text-[#1A1612]"}>
                  {priceInfo.prefix}{priceInfo.calculated}
                </span>
                {priceInfo.isSale && (
                  <span className="text-[13px] text-[#9b9089] line-through">
                    {priceInfo.original}
                  </span>
                )}
              </p>
            )}
          </div>

          {options.length > 0 && (
            <div className="space-y-5">
              {options.map((opt) => {
                const isSizeOpt = opt.title.toLowerCase().includes("size")
                const isColorOpt =
                  opt.title.toLowerCase().includes("color") ||
                  opt.title.toLowerCase().includes("colour")
                const sortedVals = sortOptionValues(
                  opt.title,
                  (opt.values || []).map((v) => v.value)
                )

                return (
                  <div key={opt.title}>
                    <div className="mb-2.5 flex items-center justify-between">
                      <span className="font-sans text-[11px] uppercase tracking-[2px] text-[#1A1612]">
                        {opt.title}
                        {selected[opt.title] && (
                          <span className="ml-2 font-normal normal-case tracking-normal text-[#6B6357]">
                            — {selected[opt.title]}
                          </span>
                        )}
                      </span>
                      {isSizeOpt && (
                        <a
                          href={`/products/${product.handle}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-sans text-[10px] uppercase tracking-[1.5px] text-[#3D5240] underline underline-offset-2 hover:text-[#8a6128]"
                        >
                          Size guide ↗
                        </a>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {sortedVals.map((val) => {
                        const available = isAvailable(opt.title, val)
                        const inS = isInStock(opt.title, val)
                        const isSelected = selected[opt.title] === val
                        const hasSwatch = isColorOpt && swatchMap[val]

                        if (hasSwatch) {
                          return (
                            <button
                              key={val}
                              type="button"
                              onClick={() =>
                                setSelected((s) => ({ ...s, [opt.title]: val }))
                              }
                              disabled={!available}
                              aria-label={`${val}${!inS ? " — out of stock" : ""}`}
                              aria-pressed={isSelected}
                              title={val}
                              className={[
                                "relative h-7 w-7 rounded-full border-2 transition",
                                isSelected ? "border-[#1A1612]" : "border-transparent hover:border-[#3D5240]",
                                !available ? "cursor-not-allowed opacity-30" : "",
                              ].join(" ")}
                              style={{ backgroundColor: swatchMap[val] }}
                            >
                              {!inS && available && (
                                <span className="absolute inset-0 flex items-center justify-center">
                                  <svg width="16" height="16" viewBox="0 0 24 24" stroke="white" strokeWidth="2" aria-hidden="true">
                                    <line x1="4" y1="20" x2="20" y2="4" />
                                  </svg>
                                </span>
                              )}
                            </button>
                          )
                        }

                        return (
                          <button
                            key={val}
                            type="button"
                            onClick={() =>
                              setSelected((s) => ({ ...s, [opt.title]: val }))
                            }
                            disabled={!available}
                            aria-pressed={isSelected}
                            aria-label={`${val}${!inS ? " — out of stock" : ""}`}
                            className={[
                              "min-w-[44px] rounded-full border px-4 py-2 font-sans text-[12px] transition",
                              isSelected
                                ? "border-[#1A1612] bg-[#1A1612] text-white"
                                : "border-[#E0D8CC] bg-white text-[#2f2924] hover:border-[#3D5240]",
                              !inS && !isSelected ? "text-[#999] line-through" : "",
                              !available ? "cursor-not-allowed opacity-30" : "",
                            ].join(" ")}
                          >
                            {val}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {selectedVariant && (
            <p
              className={`font-sans text-[11px] uppercase tracking-[2px] ${
                inStock ? "text-[#5C6B53]" : "text-[#C05050]"
              }`}
            >
              {!inStock
                ? "Out of stock"
                : lowStock
                ? `Only ${stockQty} left`
                : "In stock"}
            </p>
          )}

          <div className="mt-auto flex flex-col gap-2.5 pb-6 small:pb-0">
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={!canSubmit}
              className={[
                "flex h-12 w-full items-center justify-center font-sans text-[11px] uppercase tracking-[2.5px] transition",
                canSubmit
                  ? added
                    ? "bg-[#5C6B53] text-white"
                    : "bg-[#1A1612] text-[#FFFFFF] hover:bg-[#2a241f]"
                  : "cursor-not-allowed bg-[#EBEBEB] text-[#9E9890]",
              ].join(" ")}
            >
              {isAdding
                ? "Adding…"
                : added
                ? "✓ Added to bag"
                : !selectedVariant
                ? "Select a size"
                : !inStock
                ? "Out of stock"
                : "Add to bag"}
            </button>

            <button
              type="button"
              onClick={handleBuyNow}
              disabled={!canSubmit}
              className={[
                "flex h-12 w-full items-center justify-center border font-sans text-[11px] uppercase tracking-[2.5px] transition",
                canSubmit
                  ? "border-[#1A1612] text-[#1A1612] hover:bg-[#1A1612] hover:text-[#FFFFFF]"
                  : "cursor-not-allowed border-[#EBEBEB] text-[#9E9890]",
              ].join(" ")}
            >
              {isBuying ? "Please wait…" : "Buy now"}
            </button>

            <a
              href={`/products/${product.handle}`}
              className="text-center font-sans text-[10px] uppercase tracking-[2px] text-[#6B6357] underline underline-offset-2 hover:text-[#1A1612]"
            >
              View full details →
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Trigger button (renders inside product card image) ────────────────────────

type Props = {
  product: SProduct
  swatchMap?: Record<string, string>
}

export default function ProductCardQuickAdd({ product, swatchMap = {} }: Props) {
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const displayTitle = getDisplayTitle(product)

  useEffect(() => { setMounted(true) }, [])

  const handleOpen = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setOpen(true)
  }, [])

  // No variant/option data → can't resolve a variant to add; hide the affordance.
  const hasVariantOptions = (product.variants || []).some(
    (v) => (v.options?.length ?? 0) > 0
  )
  if (!hasVariantOptions) return null

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        aria-label={`Quick add — ${displayTitle}`}
        className="absolute bottom-2.5 right-2.5 flex items-center gap-1.5 bg-[#1A1612]/90 px-2.5 py-2 font-sans text-[9px] uppercase tracking-[2px] text-[#FFFFFF] backdrop-blur-sm transition-opacity focus-visible:opacity-100 small:opacity-0 small:group-hover:opacity-100"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
          className="shrink-0"
        >
          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
        <span className="hidden small:inline">Quick add</span>
      </button>

      {mounted &&
        open &&
        createPortal(
          <QuickViewModal
            product={product}
            swatchMap={swatchMap}
            onClose={() => setOpen(false)}
          />,
          document.body
        )}
    </>
  )
}
